/**
 * Hex-dump rendering for the byte drawer (ADR-038 Phase G).
 *
 * Pure: turns a hex window into the rows a classic dump shows — offset
 * gutter, hex columns, ASCII column — so the layout is unit-testable
 * without a render and the drawer component stays presentation only.
 *
 * Cross-references:
 *   - REQ-UI-123 (expandable, scrollable hex drawer).
 *   - Feature spec §2.10 (offset gutter, ASCII column, window paging).
 */

/** Bytes per rendered row — the conventional hex-dump width. */
export const BYTES_PER_ROW = 16;

/** One rendered row of a hex dump. */
export interface HexDumpRow {
  /** Absolute byte offset of this row's first byte, within the whole object. */
  readonly offset: number;
  /** Two-character lowercase hex per byte; short on the final row. */
  readonly hex: readonly string[];
  /** Printable ASCII per byte, with "." for non-printable (the dump convention). */
  readonly ascii: string;
}

/**
 * Split a lowercase-hex window into dump rows.
 *
 * ``windowOffset`` is where the window starts in the underlying object,
 * so the gutter shows absolute offsets — the operator is locating bytes
 * in a file, not in a scroll position. An odd-length hex string (a
 * truncated response) drops its trailing nibble rather than rendering a
 * half byte, because half a byte is not a fact about the data.
 */
export function hexDumpRows(dataHex: string, windowOffset: number): readonly HexDumpRow[] {
  const usable = dataHex.length - (dataHex.length % 2);
  const bytes: string[] = [];
  for (let i = 0; i < usable; i += 2) {
    bytes.push(dataHex.slice(i, i + 2));
  }

  const rows: HexDumpRow[] = [];
  for (let start = 0; start < bytes.length; start += BYTES_PER_ROW) {
    const slice = bytes.slice(start, start + BYTES_PER_ROW);
    rows.push({
      offset: windowOffset + start,
      hex: slice,
      ascii: slice.map(asciiFor).join(""),
    });
  }
  return rows;
}

/**
 * The ASCII column's character for one hex byte.
 *
 * Printable US-ASCII (0x20–0x7e) renders as itself; everything else is a
 * period. Rendering a control character or a high byte verbatim would
 * corrupt the column alignment the dump exists to provide.
 */
function asciiFor(hexByte: string): string {
  const code = Number.parseInt(hexByte, 16);
  if (Number.isNaN(code)) return ".";
  return code >= 0x20 && code <= 0x7e ? String.fromCharCode(code) : ".";
}

/** Format a byte offset as the dump's 8-digit hex gutter label. */
export function formatOffset(offset: number): string {
  return offset.toString(16).padStart(8, "0");
}

/** Human-readable byte size — "912 B", "1.4 KiB", "3.0 MiB". */
export function formatByteSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KiB", "MiB", "GiB", "TiB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(1)} ${units[unit]}`;
}

/** The verdict the integrity badge renders. */
export interface IntegrityBadge {
  readonly label: string;
  /** Semantic-token text colour class. */
  readonly className: string;
  /** Why the operator should or should not trust these bytes. */
  readonly title: string;
}

/**
 * Map the wire ``integrity`` value to its badge (D11 / REQ-UI-124).
 *
 * **Fail-safe: only the exact string ``verified`` reads as verified.**
 * An unknown value, a missing field, or an explicit ``unverified`` all
 * render as unconfirmed — mirroring the classification badge's posture.
 * Presenting unverified bytes as authentic evidence is the wrong failure
 * mode for a forensic surface, so the default branch is the cautious one.
 */
export function integrityBadge(integrity: string | null | undefined): IntegrityBadge {
  switch (integrity) {
    case "verified":
      return {
        label: "VERIFIED",
        className: "text-positive",
        title:
          "The reconstructed message re-hashes to the hash recorded when the connector received it.",
      };
    case "unavailable":
      return {
        label: "⚠ UNVERIFIED",
        className: "text-warning",
        title: "No stored hash is available for this record, so the bytes could not be checked.",
      };
    default:
      return {
        label: "⚠ UNVERIFIED",
        className: "text-warning",
        title:
          "These bytes did not match the hash recorded at receipt, or could not be checked. Do not treat them as authentic.",
      };
  }
}
